import React from 'react';
import { View, StyleSheet } from 'react-native';

import { DETAIL_PANE_MAX_WIDTH, useBreakpoint } from '../index';

import type { useTheme } from './theme';

/**
 * One band of the dock: the surface, the single top border, and the column cap
 * on a wide viewport.
 *
 * ⛔ It lives here and not in each bar because BOTH bands need it and they must
 * look like one block when stacked. The rest bar carried these styles alone
 * while it was the only thing down here; the moment LG-94 added the load
 * controls beside it, a second copy would have been two ideas of "the strip at
 * the bottom", free to drift apart in a padding.
 *
 * ⭐ It also carries the accessible NAME. A docked band is a region a screen
 * reader arrives at from the page above, and an unnamed one is a bare run of
 * buttons: the rest bar has announced itself since it was built, and the load
 * controls had nothing until this.
 */
export function LoggerDockBand({
  theme,
  accessibilityLabel,
  children,
}: {
  theme: ReturnType<typeof useTheme>;
  accessibilityLabel: string;
  children: React.ReactNode;
}): React.ReactElement {
  const { wide } = useBreakpoint();
  const s = makeBandStyles(theme);
  return (
    <View
      style={[s.band, wide && s.bandWide]}
      // ⚠️ The role alone does not carry the region: on native it is inert on a
      // View that is not itself a focus stop, and on web ARIA requires a
      // toolbar to be NAMED. The label is what actually speaks.
      accessibilityRole="toolbar"
      accessibilityLabel={accessibilityLabel}
    >
      <View style={s.inner}>{children}</View>
    </View>
  );
}

function makeBandStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: sp } = theme;
  return StyleSheet.create({
    band: {
      backgroundColor: p.surface,
      // Top border only: the band meets the page along one edge, and a full
      // card outline out here would read as a floating element rather than as
      // part of the screen's frame.
      //
      // ⭐ `borderStrong`, NOT `border`. Every other surface in the app is an
      // inset card whose rounded corners do half the separating; this is a
      // strip glued to the screen edge, so the line IS the whole boundary.
      // Measured: surface-on-bg is 1.06:1 and `border` carries only 1.22
      // (light) / 1.36 (dark) — under the 3:1 WCAG asks of a boundary that
      // identifies a component. `borderStrong` measures 3.46-3.70 on both
      // sides of both themes.
      borderTopWidth: 1,
      borderTopColor: p.borderStrong,
      paddingHorizontal: sp.s5,
      paddingVertical: sp.s3,
    },
    // Wide viewport: the band stays at the bottom (one mental model at every
    // width — Antenore, 2026-09-03) but its CONTENT stops stretching, so the
    // controls sit over the same column the cards use. DETAIL_PANE_MAX_WIDTH
    // is the token the Settings and Me panes already cap with; a second number
    // here would be a second idea of "the column".
    bandWide: { alignItems: 'center' },
    inner: { width: '100%', maxWidth: DETAIL_PANE_MAX_WIDTH },
  });
}
